"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  isMcatSectionScore,
  isMcatTotal,
  MCAT_SCORES_N,
  MCAT_SECTION_ROWS,
  MCAT_SECTIONS,
  MCAT_TOTAL_ROWS,
  type McatRank,
  type McatSection,
  mcatLowestTotalForRank,
  mcatSectionRank,
  mcatTotal,
  mcatTotalRank,
} from "@/lib/tool-kit/exam-scores/mcat";

/** English strings. A translated content file overrides these through `ui`. */
export const MCAT_SCORE_CALCULATOR_STRINGS = {
  inputHeading: "Your section scores",
  inputHint:
    "Each section is scored from 118 to 132. The four add up to a total from 472 to 528.",
  sections: {
    cp: "Chemical and Physical Foundations",
    cars: "Critical Analysis and Reasoning Skills",
    bb: "Biological and Biochemical Foundations",
    ps: "Psychological, Social, and Biological Foundations",
  },
  sectionShort: {
    cp: "Chem/Phys",
    cars: "CARS",
    bb: "Bio/Biochem",
    ps: "Psych/Soc",
  },
  sectionLabel: "{section} (118–132)",
  totalOnlyLabel: "Or enter a total score instead (472–528)",
  invalidSection: "{section} must be a whole number from 118 to 132.",
  invalidTotal: "A total score is a whole number from 472 to 528.",
  results: "Total score and percentile rank",
  total: "MCAT total score",
  totalPercentile: "Percentile rank of {total}",
  lessThanOne: "<1",
  working: "{parts} = {total}",
  rankLine:
    "{rank} percent of scores were equal to or less than {total}, and {higher} percent were higher.",
  rankLineLow:
    "Fewer than 1 percent of scores were equal to or less than {total}.",
  sectionHeading: "Section percentile ranks",
  sectionCard: "{section}: {score}",
  enter:
    "Enter all four section scores, or a total score, to see a percentile rank.",
  targetHeading: "Work backward from a percentile rank",
  targetLabel: "Percentile rank you want (1–100)",
  targetResult:
    "The lowest total that reaches a percentile rank of {target} is {score} (rank {rank}).",
  targetNone: "No total reaches that rank.",
  targetInvalid: "Enter a whole number from 1 to 100.",
  referenceNote:
    "AAMC percentile ranks in effect {period}, based on {count} scores from the {years} testing years. The rank is the percentage of scores equal to or less than the score.",
  period: "May 1, 2026 to April 30, 2027",
  years: "2023, 2024 and 2025",
  tableHeading: "AAMC percentile tables",
  colTotal: "Total score",
  colRank: "Percentile rank",
  colScore: "Section score",
  copyResult: "Copy result",
  copied: "Copied!",
  summary:
    "MCAT total {total}: percentile rank {rank} (AAMC, in effect {period})",
} as const;

type Strings = typeof MCAT_SCORE_CALCULATOR_STRINGS;
type Format = ReturnType<typeof useToolNumber>;

function read(raw: string): number | null {
  return raw.trim() === "" ? null : parseNumberInput(raw);
}

function rankText(rank: McatRank, strings: Strings, fmt: Format): string {
  return rank.lessThanOne ? strings.lessThanOne : fmt(rank.rank);
}

function TargetPanel({ strings, fmt }: { strings: Strings; fmt: Format }) {
  const [raw, setRaw] = useState("90");
  const value = read(raw);
  const valid =
    value !== null && Number.isInteger(value) && value >= 1 && value <= 100;
  const total = valid ? mcatLowestTotalForRank(value) : null;
  const rank = total === null ? null : mcatTotalRank(total);
  let message: string | null = null;
  if (valid && total !== null && rank) {
    message = t(strings.targetResult, {
      target: fmt(value),
      score: fmt(total),
      rank: rankText(rank, strings, fmt),
    });
  } else if (valid) {
    message = strings.targetNone;
  }
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.targetHeading}</h3>
      <TextField
        id="mcat-target"
        label={strings.targetLabel}
        numeric
        onChange={setRaw}
        value={raw}
      />
      <div aria-live="polite" className="text-foreground text-sm">
        {message}
        {valid || raw.trim() === "" ? null : (
          <Notice>{strings.targetInvalid}</Notice>
        )}
      </div>
    </section>
  );
}

function Tables({ strings, fmt }: { strings: Strings; fmt: Format }) {
  return (
    <section className="space-y-4">
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <DataTable
        head={[strings.colTotal, strings.colRank]}
        rows={MCAT_TOTAL_ROWS.map((row) => [
          fmt(row.total),
          rankText(row.rank, strings, fmt),
        ])}
      />
      <DataTable
        head={[
          strings.colScore,
          ...MCAT_SECTIONS.map((section) => strings.sectionShort[section]),
        ]}
        rows={MCAT_SECTION_ROWS.map((row) => [
          fmt(row.score),
          ...MCAT_SECTIONS.map((section) => fmt(row.ranks[section])),
        ])}
      />
    </section>
  );
}

export default function McatScoreCalculatorWidget() {
  const strings = useToolUi(MCAT_SCORE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [entries, setEntries] = useState<Record<McatSection, string>>({
    cp: "128",
    cars: "127",
    bb: "129",
    ps: "126",
  });
  const [totalRaw, setTotalRaw] = useState("");
  const values = MCAT_SECTIONS.map((section) => read(entries[section]));
  const allSections = values.every((value) => isMcatSectionScore(value));
  const sectionScores = values.filter((value): value is number =>
    isMcatSectionScore(value)
  );
  const enteredTotal = read(totalRaw);
  let total: number | null = null;
  if (allSections) {
    total = mcatTotal(sectionScores);
  } else if (isMcatTotal(enteredTotal)) {
    total = enteredTotal;
  }
  const rank = total === null ? null : mcatTotalRank(total);

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className={WIDGET_H2}>{strings.inputHeading}</h2>
        <p className="text-muted-foreground text-sm">{strings.inputHint}</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {MCAT_SECTIONS.map((section) => (
          <TextField
            id={`mcat-${section}`}
            key={section}
            label={t(strings.sectionLabel, {
              section: strings.sections[section],
            })}
            numeric
            onChange={(value) =>
              setEntries((all) => ({ ...all, [section]: value }))
            }
            value={entries[section]}
          />
        ))}
      </div>
      <TextField
        id="mcat-total"
        label={strings.totalOnlyLabel}
        numeric
        onChange={setTotalRaw}
        value={totalRaw}
      />

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {MCAT_SECTIONS.map((section) => {
          const raw = entries[section];
          return raw.trim() !== "" && !isMcatSectionScore(read(raw)) ? (
            <Notice key={section}>
              {t(strings.invalidSection, {
                section: strings.sectionShort[section],
              })}
            </Notice>
          ) : null;
        })}
        {totalRaw.trim() !== "" && !isMcatTotal(enteredTotal) ? (
          <Notice>{strings.invalidTotal}</Notice>
        ) : null}
        {total === null || rank === null ? (
          <Notice>{strings.enter}</Notice>
        ) : (
          <>
            <StatGrid>
              <StatCard emphasis label={strings.total} value={fmt(total)} />
              <StatCard
                label={t(strings.totalPercentile, { total: fmt(total) })}
                value={rankText(rank, strings, fmt)}
              />
            </StatGrid>
            {allSections ? (
              <p className="rounded-lg border border-border bg-card p-3 font-mono text-muted-foreground text-sm">
                {t(strings.working, {
                  parts: sectionScores.map((score) => fmt(score)).join(" + "),
                  total: fmt(total),
                })}
              </p>
            ) : null}
            <p className="text-muted-foreground text-sm">
              {rank.lessThanOne
                ? t(strings.rankLineLow, { total: fmt(total) })
                : t(strings.rankLine, {
                    rank: fmt(rank.rank),
                    higher: fmt(100 - rank.rank),
                    total: fmt(total),
                  })}
            </p>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyResult}
              small
              text={t(strings.summary, {
                total: fmt(total),
                rank: rankText(rank, strings, fmt),
                period: strings.period,
              })}
            />
          </>
        )}
        {allSections ? (
          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.sectionHeading}</h3>
            <StatGrid>
              {MCAT_SECTIONS.map((section, index) => {
                const score = sectionScores[index];
                const sectionRank =
                  score === undefined ? null : mcatSectionRank(section, score);
                return score === undefined || sectionRank === null ? null : (
                  <StatCard
                    key={section}
                    label={t(strings.sectionCard, {
                      section: strings.sectionShort[section],
                      score: fmt(score),
                    })}
                    value={fmt(sectionRank.rank)}
                  />
                );
              })}
            </StatGrid>
          </div>
        ) : null}
        <p className="text-muted-foreground text-sm">
          {t(strings.referenceNote, {
            period: strings.period,
            count: fmt(MCAT_SCORES_N),
            years: strings.years,
          })}
        </p>
      </section>

      <TargetPanel fmt={fmt} strings={strings} />
      <Tables fmt={fmt} strings={strings} />
    </div>
  );
}
